import type { JSX } from 'react';
import type { DealModelValue, DealSummaryResource, PipelineResource } from '@majuscule/contracts';
import { dealModelSchema } from '@majuscule/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateDealDialog } from '../components/crm/DealDialog';
import { DealFollowUp } from '../components/crm/shared';
import { PageChrome } from '../components/page-chrome';
import { internalFetch } from '../lib/api';
import {
  crmKeys,
  eurOrDash,
  formatDate,
  formatLongDate,
  isPriced,
  LOST_REASON_LABEL,
  MODEL_LABEL,
  OPEN_STAGES,
  percentLabel,
  STAGE_LABEL,
  STAGE_PROBABILITY,
  stageBadgeVariant,
} from '../lib/crm';
import { formatEurCents, sumEuros } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar } from '@/components/ui/card';
import { LoadingState } from '@/components/ui/empty';
import { SearchInput } from '@/components/ui/field';
import { Row4 } from '@/components/ui/layout-grid';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCan } from '../lib/me';
import { TableTabs } from '@/components/ui/tabs';

type View = 'kanban' | 'table';
type DealTab = 'open' | 'won' | 'lost' | 'all';
type ModelFilter = DealModelValue | 'all';

const COLUMN_COUNT = 10;

function isOpen(deal: DealSummaryResource): boolean {
  return deal.stage !== 'won' && deal.stage !== 'lost';
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n > 1 ? many : one}`;
}

function DealCard({
  deal,
  showOwner,
}: {
  deal: DealSummaryResource;
  showOwner: boolean;
}): JSX.Element {
  return (
    <Link
      to="/deals/$dealId"
      params={{ dealId: deal.id }}
      className="grid gap-1.5 rounded-md border border-border bg-card p-3 text-foreground no-underline hover:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="truncate text-xs leading-4 text-muted-foreground">
        {deal.companyName}
        {showOwner && deal.owner !== null ? ` · ${deal.owner.name}` : ''}
      </span>
      <span className="text-[13px] font-medium leading-[18px]">{deal.title}</span>
      <span className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-xs leading-4 tabular-nums">
        {isPriced(deal) ? (
          <>
            {deal.setup !== null ? <span>{formatEurCents(deal.setup)}</span> : null}
            {deal.monthly !== null ? <span>+ {formatEurCents(deal.monthly)} / mois</span> : null}
          </>
        ) : (
          <span className="font-sans text-muted-foreground">À chiffrer</span>
        )}
      </span>
      <span className="border-t border-border pt-1.5">
        <DealFollowUp deal={deal} />
      </span>
    </Link>
  );
}

function Kanban({
  deals,
  showOwner,
}: {
  deals: readonly DealSummaryResource[];
  showOwner: boolean;
}): JSX.Element {
  return (
    <div className="grid auto-cols-[minmax(212px,1fr)] grid-flow-col gap-3 overflow-x-auto p-3">
      {OPEN_STAGES.map((stage) => {
        const list = deals.filter((deal) => deal.stage === stage);
        const weighted = list
          .map((deal) => deal.weighted)
          .filter((value): value is string => value !== null);
        return (
          <section
            key={stage}
            aria-label={STAGE_LABEL[stage]}
            className="flex min-h-[460px] flex-col gap-2 rounded-lg bg-muted p-2"
          >
            <div className="grid gap-0.5 border-b border-border px-1 pb-2 pt-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold leading-[18px]">
                  {STAGE_LABEL[stage]}
                </span>
                <span className="font-mono text-xs font-medium text-muted-foreground">
                  {list.length}
                </span>
              </div>
              <div className="flex justify-between font-mono text-xs tabular-nums text-muted-foreground">
                <span>{weighted.length > 0 ? formatEurCents(sumEuros(weighted)) : '—'}</span>
                <span>{percentLabel(STAGE_PROBABILITY[stage])}</span>
              </div>
            </div>
            {list.map((deal) => (
              <DealCard key={deal.id} deal={deal} showOwner={showOwner} />
            ))}
            {list.length === 0 ? (
              <div className="rounded-md border border-dashed border-border px-2 py-4 text-center text-xs text-muted-foreground">
                Aucune affaire à cette étape
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function NextCell({ deal }: { deal: DealSummaryResource }): JSX.Element {
  switch (deal.stage) {
    case 'won':
      return (
        <Cell2
          primary="Passée en client"
          secondary={deal.closedOn !== null ? `Signée le ${formatDate(deal.closedOn)}` : undefined}
        />
      );
    case 'lost':
      return (
        <Cell2
          primary={
            deal.lostReason !== null ? `Motif : ${LOST_REASON_LABEL[deal.lostReason]}` : 'Perdue'
          }
          secondary={deal.closedOn !== null ? `Perdue le ${formatDate(deal.closedOn)}` : undefined}
        />
      );
    case 'to_qualify':
    case 'r1_planned':
    case 'r1_done':
    case 'proposal_presented':
    case 'specifications':
    case 'signature_pending':
      return <DealFollowUp deal={deal} compact />;
  }
}

export function PipelinePage(): JSX.Element {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('kanban');
  const [tab, setTab] = useState<DealTab>('open');
  const [model, setModel] = useState<ModelFilter>('all');
  // Porteur (lot 20) : un administrateur voit toutes les affaires et peut filtrer.
  const allDeals = useCan('all_deals');
  const [owner, setOwner] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const pipeline = useQuery({
    queryKey: crmKeys.pipeline,
    queryFn: () => internalFetch<PipelineResource>('/internal/crm/pipeline'),
  });

  const owners = [
    ...new Map(
      (pipeline.data?.deals ?? []).flatMap((deal) => (deal.owner === null ? [] : [[deal.owner.id, deal.owner]])),
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const query = search.trim().toLowerCase();
  const filtered = (pipeline.data?.deals ?? [])
    .filter((deal) => model === 'all' || deal.model === model)
    .filter((deal) => owner === 'all' || (deal.owner?.id ?? 'none') === owner)
    .filter(
      (deal) => query === '' || `${deal.companyName} ${deal.title}`.toLowerCase().includes(query),
    );
  const open = filtered.filter(isOpen);
  const won = filtered.filter((deal) => deal.stage === 'won');
  const lost = filtered.filter((deal) => deal.stage === 'lost');
  const sets: Record<DealTab, DealSummaryResource[]> = { open, won, lost, all: filtered };
  const rows = sets[tab];
  const pagination = usePagination(rows, 25);
  const stats = pipeline.data?.stats;
  const loaded = pipeline.data !== undefined;

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Pipeline' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus aria-hidden="true" />
            Nouvelle affaire
          </Button>
        }
      />
      <PageHead
        title="Pipeline"
        subtitle="Affaires du logiciel sur mesure et des licences. Montants HT, pondérés sur 12 mois d’abonnement."
      />

      {pipeline.isError ? (
        <Callout variant="destructive">Impossible de charger le pipeline.</Callout>
      ) : null}

      <Row4 aria-label="Indicateurs commerciaux">
        <StatCard
          label="Montant pondéré en cours"
          value={stats === undefined ? '…' : formatEurCents(stats.weighted)}
          foot={
            stats === undefined
              ? undefined
              : `${plural(stats.openCount, 'affaire ouverte', 'affaires ouvertes')}, valeur sur 12 mois`
          }
        />
        <StatCard
          label="Abonnements signés ce trimestre"
          value={stats === undefined ? '…' : formatEurCents(stats.signedMonthlyThisQuarter)}
          foot={
            stats === undefined
              ? undefined
              : `par mois, ${plural(stats.wonThisQuarter, 'affaire gagnée', 'affaires gagnées')} depuis le ${formatLongDate(stats.quarterStart)}`
          }
        />
        <StatCard
          label="Taux de signature après R1"
          value={
            stats === undefined
              ? '…'
              : stats.signatureRateAfterR1 === null
                ? '—'
                : percentLabel(stats.signatureRateAfterR1)
          }
          foot={
            stats === undefined
              ? undefined
              : stats.closedAfterR1 === 0
                ? 'aucune affaire close après un R1'
                : `${stats.wonAfterR1} sur ${plural(stats.closedAfterR1, 'affaire close', 'affaires closes')}${stats.averageCycleDays !== null ? `, cycle moyen de ${plural(stats.averageCycleDays, 'jour')}` : ''}`
          }
        />
        <StatCard
          label="À traiter"
          value={stats === undefined ? '…' : String(stats.noNextAction + stats.lateReminders)}
          foot={
            stats === undefined
              ? undefined
              : `${stats.noNextAction} sans prochaine action, ${plural(stats.lateReminders, 'rappel')} en retard`
          }
        />
      </Row4>

      <Card>
        <CardBar
          start={
            <>
              <TableTabs<View>
                aria-label="Affichage"
                value={view}
                onValueChange={setView}
                items={[
                  { value: 'kanban', label: 'Kanban' },
                  { value: 'table', label: 'Tableau' },
                ]}
              />
              {view === 'table' ? (
                <TableTabs<DealTab>
                  aria-label="Filtrer les affaires"
                  value={tab}
                  onValueChange={setTab}
                  items={[
                    { value: 'open', label: 'Ouvertes', count: loaded ? open.length : undefined },
                    { value: 'won', label: 'Gagnées', count: loaded ? won.length : undefined },
                    { value: 'lost', label: 'Perdues', count: loaded ? lost.length : undefined },
                    { value: 'all', label: 'Toutes', count: loaded ? filtered.length : undefined },
                  ]}
                />
              ) : null}
            </>
          }
          end={
            <>
              <NativeSelect
                controlSize="sm"
                wrapperClassName="w-44"
                aria-label="Modèle"
                value={model}
                onChange={(event) => {
                  const parsed = dealModelSchema.safeParse(event.target.value);
                  setModel(parsed.success ? parsed.data : 'all');
                }}
              >
                <option value="all">Tous les modèles</option>
                <option value="custom">{MODEL_LABEL.custom}</option>
                <option value="shared_license">{MODEL_LABEL.shared_license}</option>
              </NativeSelect>
              {allDeals ? (
                <NativeSelect
                  controlSize="sm"
                  wrapperClassName="w-44"
                  aria-label="Porteur"
                  value={owner}
                  onChange={(event) => {
                    setOwner(event.target.value);
                  }}
                >
                  <option value="all">Tous les porteurs</option>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </NativeSelect>
              ) : null}
              <SearchInput
                label="Rechercher une affaire"
                placeholder="Rechercher une entreprise ou une affaire"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </>
          }
        />

        {view === 'kanban' ? (
          pipeline.isLoading ? (
            <LoadingState />
          ) : (
            <Kanban deals={open} showOwner={allDeals} />
          )
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead kind="name">Affaire</TableHead>
                  <TableHead>Étape</TableHead>
                  <TableHead>Modèle</TableHead>
                  <TableHead kind="num">Mise en place</TableHead>
                  <TableHead kind="num">Abonnement / mois</TableHead>
                  <TableHead kind="num">Probabilité</TableHead>
                  <TableHead kind="num">Pondéré</TableHead>
                  <TableHead>Signature prévue</TableHead>
                  <TableHead>Prochaine action</TableHead>
                  {allDeals ? <TableHead>Porteur</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((deal) => (
                  <TableRow
                    key={deal.id}
                    onClick={() =>
                      void navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })
                    }
                  >
                    <TableCell kind="name">
                      <Cell2
                        primary={
                          <Link
                            to="/deals/$dealId"
                            params={{ dealId: deal.id }}
                            className={rowLinkClass}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {deal.title}
                          </Link>
                        }
                        secondary={deal.companyName}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant={stageBadgeVariant(deal.stage)}>
                        {STAGE_LABEL[deal.stage]}
                      </Badge>
                    </TableCell>
                    <TableCell kind="sub">{MODEL_LABEL[deal.model]}</TableCell>
                    <TableCell kind="num">
                      {deal.setup === null && isOpen(deal) ? 'À chiffrer' : eurOrDash(deal.setup)}
                    </TableCell>
                    <TableCell kind="num">{eurOrDash(deal.monthly)}</TableCell>
                    <TableCell kind="num">{percentLabel(deal.probability)}</TableCell>
                    <TableCell kind="num">{eurOrDash(deal.weighted)}</TableCell>
                    <TableCell kind="mono">
                      {deal.expectedCloseDate !== null ? formatDate(deal.expectedCloseDate) : '—'}
                    </TableCell>
                    <TableCell>
                      <NextCell deal={deal} />
                    </TableCell>
                    {allDeals ? <TableCell kind="sub">{deal.owner?.name ?? '—'}</TableCell> : null}
                  </TableRow>
                ))}
                {pipeline.isLoading ? (
                  <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
                ) : null}
                {loaded && rows.length === 0 ? (
                  <TableEmptyRow colSpan={COLUMN_COUNT}>
                    Aucune affaire ne correspond.
                  </TableEmptyRow>
                ) : null}
              </TableBody>
            </Table>
            <Pager itemLabel="affaires" {...pagination.pagerProps} pageSizeOptions={[10, 25, 50]} />
          </>
        )}
      </Card>

      <CreateDealDialog
        open={creating}
        companyId={null}
        onClose={() => {
          setCreating(false);
        }}
      />
    </>
  );
}
